// Casa La Vie inline shipping estimator (T&W-style).
// Web component name kept as <shipping-estimator>. The legacy
// <shipping-calculator> name is also registered as a fallback so any
// older snippet that still uses the old tag keeps working.

(function () {
  const STORAGE_KEY = 'clv:shipping-estimator';
  const POSTCODE_TTL_MS = 1000 * 60 * 60 * 24 * 30; // 30 days
  const STATE_NAMES = {
    ACT: 'Australian Capital Territory',
    NSW: 'New South Wales',
    NT: 'Northern Territory',
    QLD: 'Queensland',
    SA: 'South Australia',
    TAS: 'Tasmania',
    VIC: 'Victoria',
    WA: 'Western Australia'
  };

  // Module-scoped postcode dataset cache (shared across all estimator instances)
  let postcodesPromise = null;
  function loadPostcodes(src) {
    if (!postcodesPromise) {
      postcodesPromise = fetch(src)
        .then((r) => r.json())
        .catch((err) => {
          postcodesPromise = null; // allow retry
          throw err;
        });
    }
    return postcodesPromise;
  }

  function debounce(fn, wait) {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  function readSaved() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || !parsed.savedAt) return null;
      if (Date.now() - parsed.savedAt > POSTCODE_TTL_MS) return null;
      return parsed;
    } catch (_) {
      return null;
    }
  }

  function writeSaved(data) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...data, savedAt: Date.now() }));
    } catch (_) {}
  }

  function formatMoney(cents) {
    if (window.theme && theme.settings && theme.settings.moneyFormat) {
      return theme.settings.moneyFormat.replace(/\{\{\s*\w+\s*\}\}/, (Number(cents) / 100).toFixed(2));
    }
    return '$' + (Number(cents) / 100).toFixed(2);
  }

  class ShippingEstimator extends HTMLElement {
    connectedCallback() {
      // Cache nodes
      this.toggleBtn = this.querySelector('.js-shipping-toggle');
      this.label = this.querySelector('.js-shipping-label');
      this.form = this.querySelector('.js-shipping-form');
      this.country = this.querySelector('.js-country-select');
      this.province = this.querySelector('.js-province-select');
      this.provinceContainer = this.querySelector('.js-province-container');
      this.suburbInput = this.querySelector('.js-suburb-input');
      this.suburbContainer = this.querySelector('.js-suburb-container');
      this.suggestions = this.querySelector('.js-suburb-suggestions');
      this.zipInput = this.querySelector('.js-zip-input');
      this.zipContainer = this.querySelector('.js-zip-container');
      this.submitBtn = this.querySelector('.js-shipping-submit');
      this.cancelBtn = this.querySelector('.js-shipping-cancel');
      this.ratesEl = this.querySelector('.js-rates');
      this.errorsEl = this.querySelector('.js-errors');

      this.postcodeSrc = this.dataset.postcodeSrc;
      this.defaultCountry = this.dataset.defaultCountry || 'Australia';

      // Restore prior selection (or fall back to defaults)
      const saved = readSaved();
      this.state = saved || null;

      // Render initial collapsed label
      this.renderLabel();

      // Bind events
      this.toggleBtn.addEventListener('click', () => this.expand());
      this.cancelBtn.addEventListener('click', () => this.collapse());
      this.submitBtn.addEventListener('click', () => this.calculate());
      this.country.addEventListener('change', () => this.onCountryChange());

      const debouncedSearch = debounce(() => this.runSearch(), 120);
      this.suburbInput.addEventListener('input', debouncedSearch);
      this.suburbInput.addEventListener('keydown', (e) => this.onSuburbKeydown(e));
      this.suburbInput.addEventListener('focus', () => this.runSearch());
      this.suggestions.addEventListener('click', (e) => this.onSuggestionClick(e));
      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) this.hideSuggestions();
      });

      this.zipInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.calculate();
        }
      });

      // Set country from saved (or default), then sync UI
      const initialCountry = (saved && saved.country) || this.defaultCountry;
      if ([...this.country.options].some((o) => o.value === initialCountry)) {
        this.country.value = initialCountry;
      }
      this.onCountryChange();

      // Pre-fill the visible inputs from saved state
      if (saved) {
        if (saved.country === 'Australia' && saved.suburb) {
          this.suburbInput.value = saved.suburb + ', ' + saved.stateCode + ' ' + saved.zip;
        } else if (saved.zip) {
          this.zipInput.value = saved.zip;
        }
      }

      // Apply any saved estimate now, and re-fetch it if the cart differs from when it was
      // last priced (item 5: recalc when items are added or removed).
      this.syncOrRecalc();

      // Stay in sync with AJAX cart updates (re-price for the new cart contents).
      document.addEventListener('on:cart:after-merge', this.onCartChange);
    }

    disconnectedCallback() {
      document.removeEventListener('on:cart:after-merge', this.onCartChange);
    }

    expand() {
      this.form.hidden = false;
      this.toggleBtn.setAttribute('aria-expanded', 'true');
      this.toggleBtn.hidden = true;
      // Focus the most relevant input
      if (this.country.value === 'Australia') {
        this.suburbInput.focus();
        this.suburbInput.select();
      } else {
        this.zipInput.focus();
        this.zipInput.select();
      }
    }

    collapse() {
      this.form.hidden = true;
      this.toggleBtn.hidden = false;
      this.toggleBtn.setAttribute('aria-expanded', 'false');
      this.hideSuggestions();
      this.errorsEl.hidden = true;
      this.toggleBtn.focus();
    }

    onCountryChange() {
      const isAu = this.country.value === 'Australia';
      this.suburbContainer.hidden = !isAu;
      this.zipContainer.hidden = isAu;
      // Hide the province dropdown — for AU we derive it from the suburb;
      // for other countries Shopify accepts shipping_address[zip] without province.
      if (this.provinceContainer) this.provinceContainer.hidden = true;
      this.hideSuggestions();
    }

    async runSearch() {
      if (this.country.value !== 'Australia') return;
      const q = (this.suburbInput.value || '').trim();
      if (q.length < 2) {
        this.hideSuggestions();
        return;
      }
      let data;
      try {
        data = await loadPostcodes(this.postcodeSrc);
      } catch (_) {
        this.hideSuggestions();
        return;
      }
      const upper = q.toUpperCase();
      const isNumeric = /^\d{2,4}$/.test(q);
      const matches = [];
      const limit = 8;
      for (let i = 0; i < data.length && matches.length < limit; i++) {
        const row = data[i]; // [suburb, stateCode, postcode]
        if (isNumeric) {
          if (row[2].startsWith(q)) matches.push(row);
        } else if (row[0].toUpperCase().startsWith(upper)) {
          matches.push(row);
        }
      }
      // Fallback: if no prefix matches for a name, try contains (still bounded)
      if (!isNumeric && matches.length === 0) {
        for (let i = 0; i < data.length && matches.length < limit; i++) {
          const row = data[i];
          if (row[0].toUpperCase().includes(upper)) matches.push(row);
        }
      }
      this.renderSuggestions(matches);
    }

    renderSuggestions(matches) {
      if (!matches.length) {
        this.hideSuggestions();
        return;
      }
      const html = matches
        .map(
          (row, i) =>
            `<li role="option" data-idx="${i}" data-suburb="${row[0]}" data-state="${row[1]}" data-zip="${row[2]}">` +
            `<span class="shipping-estimator__suggestion-suburb">${row[0]}</span>` +
            `<span class="shipping-estimator__suggestion-meta">${row[1]} ${row[2]}</span>` +
            `</li>`
        )
        .join('');
      this.suggestions.innerHTML = html;
      this.suggestions.hidden = false;
      this.activeIdx = -1;
    }

    hideSuggestions() {
      this.suggestions.hidden = true;
      this.suggestions.innerHTML = '';
      this.activeIdx = -1;
    }

    onSuburbKeydown(e) {
      const items = this.suggestions.querySelectorAll('li');
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (!items.length) return;
        this.activeIdx = (this.activeIdx + 1) % items.length;
        this.highlightActive(items);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (!items.length) return;
        this.activeIdx = (this.activeIdx - 1 + items.length) % items.length;
        this.highlightActive(items);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (this.activeIdx >= 0 && items[this.activeIdx]) {
          this.selectSuggestion(items[this.activeIdx]);
        } else {
          this.calculate();
        }
      } else if (e.key === 'Escape') {
        this.hideSuggestions();
      }
    }

    highlightActive(items) {
      items.forEach((el, i) => el.classList.toggle('is-active', i === this.activeIdx));
      const active = items[this.activeIdx];
      if (active) active.scrollIntoView({ block: 'nearest' });
    }

    onSuggestionClick(e) {
      const li = e.target.closest('li');
      if (!li) return;
      this.selectSuggestion(li);
    }

    selectSuggestion(li) {
      const suburb = li.dataset.suburb;
      const stateCode = li.dataset.state;
      const zip = li.dataset.zip;
      this.suburbInput.value = `${suburb}, ${stateCode} ${zip}`;
      this.hideSuggestions();
      this.calculate({ suburb, stateCode, zip, country: 'Australia' });
    }

    async calculate(forced) {
      this.errorsEl.hidden = true;
      this.submitBtn.disabled = true;
      this.submitBtn.classList.add('is-loading');

      const country = this.country.value;
      let suburb = null;
      let stateCode = null;
      let zip = null;
      let provinceName = '';

      if (forced) {
        suburb = forced.suburb;
        stateCode = forced.stateCode;
        zip = forced.zip;
        provinceName = STATE_NAMES[stateCode] || '';
      } else if (country === 'Australia') {
        // Try to interpret what the user typed
        const raw = (this.suburbInput.value || '').trim();
        // Pattern from selectSuggestion: "Suburb, STATE 1234"
        const match = raw.match(/^(.*),\s*([A-Z]{2,3})\s+(\d{4})$/);
        if (match) {
          suburb = match[1].trim();
          stateCode = match[2];
          zip = match[3];
          provinceName = STATE_NAMES[stateCode] || '';
        } else if (/^\d{4}$/.test(raw)) {
          // Bare postcode — look up first matching suburb
          try {
            const data = await loadPostcodes(this.postcodeSrc);
            const hit = data.find((r) => r[2] === raw);
            if (hit) {
              suburb = hit[0];
              stateCode = hit[1];
              zip = hit[2];
              provinceName = STATE_NAMES[stateCode] || '';
              this.suburbInput.value = `${suburb}, ${stateCode} ${zip}`;
            } else {
              zip = raw;
            }
          } catch (_) {
            zip = raw;
          }
        } else {
          this.showError({ form: window.theme && theme.strings && theme.strings.shippingCalculator && theme.strings.shippingCalculator.invalidSuburb || 'Pick a suburb from the list.' });
          this.submitBtn.disabled = false;
          this.submitBtn.classList.remove('is-loading');
          return;
        }
      } else {
        zip = (this.zipInput.value || '').trim();
        if (!zip) {
          this.showError({ form: 'Enter a postcode.' });
          this.submitBtn.disabled = false;
          this.submitBtn.classList.remove('is-loading');
          return;
        }
      }

      const params = new URLSearchParams();
      params.set('shipping_address[country]', country);
      if (provinceName) params.set('shipping_address[province]', provinceName);
      if (zip) params.set('shipping_address[zip]', zip);

      const cartRoute = (window.theme && theme.routes && theme.routes.cart) || '/cart';
      try {
        const res = await fetch(`${cartRoute}/shipping_rates.json?${params.toString()}`);
        const data = await res.json();
        if (!res.ok) {
          this.showError(data);
          return;
        }
        this.handleRates(data, { country, suburb, stateCode, zip });
      } catch (err) {
        console.error(err);
        this.showError({ network: 'Could not contact the shipping service. Try again.' });
      } finally {
        this.submitBtn.disabled = false;
        this.submitBtn.classList.remove('is-loading');
      }
    }

    handleRates(data, addr) {
      const rates = (data && data.shipping_rates) || [];
      if (!rates.length) {
        // No rates available for this address
        this.ratesEl.hidden = false;
        const heading = this.ratesEl.querySelector('.js-rates-heading');
        const text = this.ratesEl.querySelector('.js-rates-text');
        heading.textContent = '';
        heading.hidden = true;
        text.textContent =
          (window.theme && theme.strings && theme.strings.shippingCalculator && theme.strings.shippingCalculator.noRates) ||
          'No shipping rates available for this address.';
        this.applyRateToSummary(null, null);
        // Still save the address so they don't have to re-enter
        const saved = { ...addr, shippingPrice: null, shippingName: null, subtotalCents: this.getSubtotalCents() };
        writeSaved(saved);
        this.state = saved;
        this.renderLabel();
        // Don't auto-collapse — let them see the message
        return;
      }
      // Pick the cheapest rate as the default for the summary
      const cheapest = rates.reduce((a, b) => (parseFloat(a.price) <= parseFloat(b.price) ? a : b));
      // Shopify returns price as a string in dollars (e.g. "9.95")
      const cents = Math.round(parseFloat(cheapest.price) * 100);

      this.applyRateToSummary(cents, cheapest.name);
      const saved = { ...addr, shippingPrice: cents, shippingName: cheapest.name, subtotalCents: this.getSubtotalCents() };
      writeSaved(saved);
      this.state = saved;
      this.renderLabel();
      this.ratesEl.hidden = true;
      this.collapse();
    }

    // Current cart subtotal (in cents) from the summary, or NaN if unavailable.
    getSubtotalCents() {
      const summaryRoot = this.closest('[data-cart-summary]') || document;
      const el = summaryRoot.querySelector('[data-cart-subtotal-cents]');
      return el ? parseInt(el.dataset.cartSubtotalCents, 10) : NaN;
    }

    applyRateToSummary(cents, name) {
      // A recreated estimator (cart re-render) may have an in-flight fetch from a detached
      // instance — don't let it write into the live summary.
      if (!this.isConnected) return;
      // Shipping/Total/Subtotal slots all live in the Order Summary panel (outside this element).
      const summaryRoot = this.closest('[data-cart-summary]') || document;
      const shippingLine = summaryRoot.querySelector('[data-shipping-line]');
      const shippingAmount = summaryRoot.querySelector('[data-shipping-amount]');
      const totalLine = summaryRoot.querySelector('[data-total-line]');
      const totalAmount = summaryRoot.querySelector('[data-total-amount]');
      const shippingNameEl = summaryRoot.querySelector('[data-shipping-name]');
      const subtotalEl = summaryRoot.querySelector('[data-cart-subtotal-cents]');
      const subtotalCents = subtotalEl ? parseInt(subtotalEl.dataset.cartSubtotalCents, 10) : NaN;

      if (cents == null) {
        // No estimate (or unestimateable) — hide the shipping line, reset Total to subtotal.
        if (shippingLine) shippingLine.hidden = true;
        if (shippingNameEl) shippingNameEl.textContent = '';
        if (totalAmount && !Number.isNaN(subtotalCents)) {
          totalAmount.textContent = formatMoney(subtotalCents);
        }
        return;
      }

      if (shippingAmount) shippingAmount.textContent = formatMoney(cents);
      if (shippingNameEl) shippingNameEl.textContent = name ? ` (${name})` : '';
      if (shippingLine) shippingLine.hidden = false;

      if (totalAmount && !Number.isNaN(subtotalCents)) {
        totalAmount.textContent = formatMoney(subtotalCents + cents);
        if (totalLine) totalLine.hidden = false;
      }
    }

    // On cart change (item added/removed) re-price for the new cart, not just re-apply the old rate.
    onCartChange = () => {
      this.syncOrRecalc();
    };

    // Apply the last known estimate immediately, then re-fetch it if the cart total has changed
    // since it was priced. Used on mount and on every cart update.
    syncOrRecalc() {
      const s = this.state;
      if (!s) return;
      // Keep the Total visually in sync with whatever we last knew (avoids a flash of no shipping).
      if (s.shippingPrice != null) {
        this.applyRateToSummary(s.shippingPrice, s.shippingName);
      }
      const hasAddress = s.country && (s.zip || s.stateCode);
      if (!hasAddress) return;
      // Re-price only when the cart total differs from when shipping was last calculated.
      const currentSubtotal = this.getSubtotalCents();
      if (Number.isNaN(currentSubtotal) || s.subtotalCents !== currentSubtotal) {
        this.recalcFromSaved();
      }
    }

    // Re-query Shopify for the saved address against the current cart and update the summary.
    async recalcFromSaved() {
      const s = this.state;
      if (!s || !s.country) return;
      const provinceName = s.stateCode ? (STATE_NAMES[s.stateCode] || '') : '';
      if (!s.zip && !provinceName) return;

      const params = new URLSearchParams();
      params.set('shipping_address[country]', s.country);
      if (provinceName) params.set('shipping_address[province]', provinceName);
      if (s.zip) params.set('shipping_address[zip]', s.zip);

      const cartRoute = (window.theme && theme.routes && theme.routes.cart) || '/cart';
      try {
        const res = await fetch(`${cartRoute}/shipping_rates.json?${params.toString()}`);
        const data = await res.json();
        if (!res.ok || !this.isConnected) return; // keep the previous estimate on error / if detached
        const rates = (data && data.shipping_rates) || [];
        const subtotalCents = this.getSubtotalCents();
        if (!rates.length) {
          this.applyRateToSummary(null, null);
          const saved = { ...s, shippingPrice: null, shippingName: null, subtotalCents };
          writeSaved(saved);
          this.state = saved;
          this.renderLabel();
          return;
        }
        const cheapest = rates.reduce((a, b) => (parseFloat(a.price) <= parseFloat(b.price) ? a : b));
        const cents = Math.round(parseFloat(cheapest.price) * 100);
        this.applyRateToSummary(cents, cheapest.name);
        const saved = { ...s, shippingPrice: cents, shippingName: cheapest.name, subtotalCents };
        writeSaved(saved);
        this.state = saved;
        this.renderLabel();
      } catch (_) {
        // keep the previous estimate on failure
      }
    }

    renderLabel() {
      if (!this.state) {
        this.label.textContent =
          (window.theme && theme.strings && theme.strings.shippingCalculator && theme.strings.shippingCalculator.estimatePrompt) ||
          'Estimate shipping';
        return;
      }
      let parts = [];
      if (this.state.country === 'Australia' && this.state.suburb) {
        parts.push(this.state.suburb);
        if (this.state.stateCode) parts.push(this.state.stateCode);
        if (this.state.zip) parts.push(this.state.zip);
      } else {
        if (this.state.zip) parts.push(this.state.zip);
        if (this.state.country) parts.push(this.state.country);
      }
      const prefix =
        (window.theme && theme.strings && theme.strings.shippingCalculator && theme.strings.shippingCalculator.shippingTo) ||
        'Shipping to:';
      this.label.textContent = `${prefix} ${parts.join(' ')}`;
    }

    showError(data) {
      const list = Object.keys(data || {}).map((k) => `<li>${data[k]}</li>`).join('');
      const textEl = this.errorsEl.querySelector('.js-errors-text');
      if (textEl) {
        textEl.innerHTML = `<ul class="styled-list">${list}</ul>`;
      } else {
        this.errorsEl.innerHTML = `<ul class="styled-list">${list}</ul>`;
      }
      this.errorsEl.hidden = false;
    }
  }

  if (!customElements.get('shipping-estimator')) {
    customElements.define('shipping-estimator', ShippingEstimator);
  }
  // Backwards-compat: any older snippet still using <shipping-calculator> gets the same behavior
  if (!customElements.get('shipping-calculator')) {
    customElements.define('shipping-calculator', class extends ShippingEstimator {});
  }
})();
